import { X } from "lucide-react"

import CheckGreen from "@/assets/img/astro-map/check-green.svg"
import AtomIllo from "@/assets/img/astro-map/scan-popup-atom.png"
import PopoverBg from "@/assets/img/astro-map/scan-popup-bg.png"
import CompassIllo from "@/assets/img/astro-map/scan-q2-compass.png"
import PlanetIllo from "@/assets/img/astro-map/scan-q1-planet.png"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { useScanTimeline } from "@/features/quiz/hooks/use-scan-timeline"
import { PINS, Z_FOCUS, Z_OVERVIEW, type CosmicMapView } from "./cosmic-map"
import { CosmicMapScene, Starfield } from "./cosmic-map-scene"
import type { StepComponentProps } from "./step-contract"

/**
 * Stage durations (ms). Larger = slower; the storyboard plays through in full
 * because every visual is positioned off the stage's 0→1 progress. Tuned for
 * smoothness, roughly half the Figma 10/20/10 (40s): the 11s fly-over gives each
 * of the four pins ~2.2s (a 1200ms camera glide + ~1s to settle) and leaves the
 * pull-back room to finish framing all four before the popup; the 6s scan eases
 * in (a smooth fade, a calm beat, then the sweep); the 4s close still lets the
 * line/planet draw-in (~2.8s) land. ~21s total.
 */
const STAGE_DURATIONS = [6_000, 14_000, 4_000] as const

/** The three scan tasks (one per stage) — the Figma scanning checklist. */
const TASKS = [
  "Calculating your planetary lines",
  "Mapping your power locations",
  "Analyzing location influences",
] as const

const clamp01 = (n: number) => Math.min(1, Math.max(0, n))

/**
 * Astro Map "analysis" step (Figma "Scanning Animation Storyboard"): a full-bleed
 * cosmic scanner over a world map ({@link CosmicMapScene}), driven by
 * {@link useScanTimeline}. Three stages play in sequence over their progress bars:
 *  1. the map fades in and a scan line sweeps it;
 *  2. the camera flies over the map, zooming to each of four power-location pins
 *     in turn (a colour-matched halo blooms under the centred pin), then pulls
 *     back to frame all four;
 *  3. planetary lines + a moon draw in.
 * When a stage's bar fills, its bottom-sheet popup opens and the scan freezes
 * until the user taps through. Two yes/no inter-questions and a closing "important
 * insight" popup punctuate the stages; answers are cosmetic (not persisted).
 * Dismissing the last popup calls `onAdvance` — which leads to the email step,
 * where the same formed map is shown again. Wired via the quiz's `stepOverrides`.
 */
export function AstroScanningStep({ onAdvance, onBack }: StepComponentProps) {
  const { stageIndex, stageProgress, phase, resume } = useScanTimeline(STAGE_DURATIONS, onAdvance)

  // ── Stage 1: the map eases in smoothly (smoothstep, so it never pops in), holds
  //    for a calm beat, then the scan line sweeps top→bottom over the rest. ──
  const FADE_END = 0.32
  const SCAN_START = 0.44
  const smoothstep = (t: number) => t * t * (3 - 2 * t)
  const mapOpacity = stageIndex > 0 ? 0.85 : smoothstep(clamp01(stageProgress / FADE_END)) * 0.85
  const scanActive = stageIndex === 0 && stageProgress > SCAN_START
  const scanTop = `${clamp01((stageProgress - SCAN_START) / (1 - SCAN_START)) * 108 - 4}%`

  // ── Stage 2: camera fly-over, one slot per pin within the first PINS_END of the
  //    stage. Each slot *pulses*: the first FOCUS_FRAC zooms in on the pin (its halo
  //    blooms); the rest pulls back to the overview and drifts toward the next pin,
  //    so the halo fades and the map glides between pins (Scanning_Frame_4…12).
  //    After PINS_END the camera settles on the full overview before the popup. ──
  const PINS_END = 0.85
  const FOCUS_FRAC = 0.55
  const flying = stageIndex === 1 && phase === "scanning" && stageProgress < PINS_END
  const slotT = flying ? (stageProgress / PINS_END) * PINS.length : PINS.length
  const slot = Math.min(PINS.length - 1, Math.floor(slotT))
  const within = Math.min(1, slotT - slot)
  const focusing = flying && within < FOCUS_FRAC

  // Camera: zoom into the active pin while focusing; otherwise pull back to the
  // overview and drift toward the next pin. The scene owns the transform math.
  const focus = focusing ? PINS[slot] : null
  const pan = flying && !focusing ? PINS[Math.min(PINS.length - 1, slot + 1)] : null
  const zoom = focus ? Z_FOCUS : Z_OVERVIEW

  // A pin is shown once the camera has reached its slot (or any overview past stage 1).
  const pinShown = (i: number) =>
    stageIndex > 1 || (stageIndex === 1 && (!flying || i <= slot))

  // ── Stage 3: lines + moon draw in. ──
  const onLines = stageIndex === 2

  const view: CosmicMapView = {
    mapOpacity,
    focus,
    pan,
    zoom,
    pinShown,
    onLines,
    scanActive,
    scanTop,
  }

  // Progress bars: cleared stages stay full + get a check; the active one fills live.
  const barFill = (i: number) => (i < stageIndex ? 1 : i === stageIndex ? stageProgress : 0)
  const stageCleared = (i: number) => i < stageIndex || (i === stageIndex && phase !== "scanning")

  return (
    <div className="relative flex flex-1 flex-col justify-start overflow-hidden bg-dark-base text-purple-light">
      {/* Cosmic backdrop: a soft purple glow + a sparse starfield behind everything. */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-24 left-1/2 size-[420px] -translate-x-1/2 rounded-full bg-main-purple/20 blur-[120px]" />
        <Starfield />
      </div>

      {/* ── Map band: grows to the map's natural (width-fit) height and no taller,
          so the map fills the band exactly rather than letterboxing inside it. The
          column is top-aligned (justify-start), so on a tall screen the leftover
          height collects below the checklist as cosmic space instead of splitting
          above and below the map+checklist group. ── */}
      <CosmicMapScene view={view} className="min-h-0 max-h-[min(101.45vw,446px)] flex-1" />

      {/* Close button at the screen edge. */}
      <button
        type="button"
        onClick={onBack}
        aria-label="Close"
        className="absolute right-4 top-4 z-10 flex size-9 items-center justify-center rounded-full text-purple-light/70 transition-colors hover:bg-purple-light/10 hover:text-purple-light"
      >
        <X className="size-5" />
      </button>

      {/* ── Title + the three-item scan checklist with live progress bars. ── */}
      <div className="relative z-10 flex shrink-0 flex-col gap-6 px-4 pb-8 pt-2">
        <h1 className="text-center font-display text-2xl font-medium text-purple-light">
          Preparing your cosmic location guide...
        </h1>

        <div className="flex flex-col gap-4">
          {TASKS.map((task, i) => (
            <div key={task} className="flex flex-col gap-2">
              <div className="flex items-center justify-between gap-3">
                <span className="text-base text-purple-light/70">{task}</span>
                {stageCleared(i) && <img src={CheckGreen} alt="" className="size-4 shrink-0" />}
              </div>
              <div className="h-2 w-full overflow-hidden rounded-[6px] bg-purple-light/10">
                <div
                  className="h-full rounded-[6px] bg-main-purple"
                  style={{ width: `${barFill(i) * 100}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Inter-questions + popup: bottom-sheet modals over a frozen scan. ── */}
      {phase === "popup" && stageIndex === 0 && (
        <QuestionSheet
          illustration={PlanetIllo}
          title="Do you believe the universe guides you to the right places?"
          onAnswer={resume}
        />
      )}

      {phase === "popup" && stageIndex === 1 && (
        <QuestionSheet
          illustration={CompassIllo}
          title={'Have you ever felt a location was "meant for you"?'}
          onAnswer={resume}
        />
      )}

      {phase === "popup" && stageIndex === 2 && (
        <ModalSheet banner={AtomIllo} contentClassName="pt-[128px]">
          <div className="flex flex-col gap-2 text-center">
            <h2 className="font-display text-2xl font-medium text-purple-light">Important insight</h2>
            <p className="text-base text-purple-light/70">
              Your map shows challenging planetary lines crossing places you&rsquo;ve lived. These
              locations may have been limiting your potential without you realizing it. What
              you&rsquo;re about to see may be eye-opening.
            </p>
          </div>
          <Button variant="gradient" size="xl" onClick={resume}>
            I understand
          </Button>
        </ModalSheet>
      )}
    </div>
  )
}

/**
 * A yes/no inter-question (Figma Scanning_Question1/2): the cosmic-background
 * popover banner with the question's illustration centred on it, the title, and
 * No/Yes. The frozen scan sits behind the dimmed scrim.
 */
function QuestionSheet({
  illustration,
  title,
  onAnswer,
}: {
  illustration: string
  title: string
  onAnswer: () => void
}) {
  return (
    <ModalSheet banner={PopoverBg} contentClassName="pt-6">
      <div className="flex flex-col items-center gap-4">
        <img src={illustration} alt="" className="size-[120px] object-contain" />
        <h2 className="text-center font-display text-2xl font-medium text-purple-light">{title}</h2>
      </div>
      <div className="flex w-full gap-2">
        <Button
          variant="ghost"
          size="xl"
          className="flex-1 bg-purple-light/10 text-purple-light hover:bg-purple-light/15 hover:text-purple-light"
          onClick={onAnswer}
        >
          No
        </Button>
        <Button variant="gradient" size="xl" className="flex-1" onClick={onAnswer}>
          Yes
        </Button>
      </div>
    </ModalSheet>
  )
}

/**
 * Shared bottom-sheet shell (Figma popovers): dimmed/blurred scrim + a rounded
 * dark surface with a full-width cosmic `banner` (414×240) bleeding off the top.
 * `contentClassName` sets the top padding so content clears/overlaps the banner
 * (questions sit their illustration on it; the insight popup's banner IS its
 * illustration, so its text is pushed down past the atom).
 */
function ModalSheet({
  banner,
  contentClassName,
  children,
}: {
  banner: string
  contentClassName?: string
  children: React.ReactNode
}) {
  return (
    <div className="absolute inset-0 z-20 flex flex-col justify-end">
      <div className="astro-sheet-fade absolute inset-0 bg-dark-base/70 backdrop-blur-sm" />
      <div className="astro-sheet-up relative flex w-full flex-col overflow-hidden rounded-t-[24px] bg-dark-surface">
        <img
          src={banner}
          alt=""
          className="pointer-events-none absolute inset-x-0 top-0 h-[240px] w-full object-cover"
        />
        <div
          className={cn(
            "relative flex w-full flex-col items-center gap-6 px-4 pb-6 text-center",
            contentClassName,
          )}
        >
          {children}
        </div>
      </div>
    </div>
  )
}
